import { useState } from "react";
import { useParams } from "react-router-dom";
import { useGetTagsQuery, useUpdateTagMutation, useDeleteTagMutation } from "@/features/tags/api";
import { Card, Box, Typography, Stack, IconButton, CircularProgress, Snackbar } from "@mui/joy";
import { DeleteAction } from "@/components/DeleteAction";
import { useCrudHouseholdMutation } from "@/hooks/useCrudHouseholdMutation";
import { capitalizeFirstLetter } from "@/utils/strings";
import { statusCodeToMessage } from "@/utils/errors";
import { InlineEditControls } from "@/components/InlineEditControls";
import { EmojiPickerButton } from "@/components/EmojiPickerButton";
import { EmojiDisplay } from "@/components/EmojiDisplay";
import { AddTagForm } from "../components/AddTagForm";
import EditIcon from "@mui/icons-material/Edit";

const ERROR_OVERRIDES = { 409: "A tag with this name already exists." };

export function SettingsTagsPage() {
  const { householdId } = useParams();
  const { data, isLoading } = useGetTagsQuery({ householdId });

  const tags = data?.data || [];

  return (
    <Card variant="outlined" sx={{ p: 2.5 }}>
      <Box sx={{ mb: 2 }}>
        <Typography level="h4">Tags</Typography>
        <Typography level="body-sm">Create and manage tags for organizing your recipes</Typography>
      </Box>
      <Stack gap={2}>
        <AddTagForm householdId={householdId} />
        {isLoading ? <CircularProgress /> : <TagsList tags={tags} />}
      </Stack>
    </Card>
  );
}

function TagsList({ tags }) {
  const [editingTagId, setEditingTagId] = useState(null);

  if (tags.length === 0) {
    return (
      <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
        No tags yet. Create one to get started!
      </Typography>
    );
  }

  return (
    <Box sx={{ columnCount: { xs: 1, sm: 2, md: 3 }, columnGap: 1 }}>
      {tags.map((tag) => (
        <TagRow
          key={tag.id}
          tag={tag}
          isEditing={editingTagId === tag.id}
          onStartEdit={() => setEditingTagId(tag.id)}
          onStopEdit={() => setEditingTagId(null)}
        />
      ))}
    </Box>
  );
}

function TagRow({ tag, isEditing, onStartEdit, onStopEdit }) {
  const { householdId } = useParams();
  const [updateTag, { isLoading: isUpdating }] = useUpdateTagMutation();
  const useDelete = useCrudHouseholdMutation(householdId, useDeleteTagMutation);
  const [editValue, setEditValue] = useState(tag.name);
  const [editEmoji, setEditEmoji] = useState(tag.emoji || "");
  const [error, setError] = useState(null);

  const handleSave = async () => {
    const name = editValue.toLowerCase().trim();
    if (!name) return;

    setError(null);
    try {
      await updateTag({ householdId, id: tag.id, data: { name, emoji: editEmoji } }).unwrap();
      onStopEdit();
    } catch (err) {
      setError(statusCodeToMessage(err.status, ERROR_OVERRIDES));
    }
  };

  const handleStartEdit = () => {
    setEditValue(capitalizeFirstLetter(tag.name));
    setEditEmoji(tag.emoji || "");
    onStartEdit();
  };

  return (
    <Box
      sx={{
        py: 0.75,
        px: 1,
        mb: 0.5,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        breakInside: "avoid",
        bgcolor: "background.level1",
        borderRadius: "sm",
        "&:hover": {
          bgcolor: "background.level2",
        },
      }}
    >
      {isEditing ? (
        <>
          <EmojiPickerButton emoji={editEmoji} onEmojiSelect={setEditEmoji} disabled={isUpdating} />
          <InlineEditControls
            value={editValue}
            onChange={setEditValue}
            onSave={handleSave}
            onCancel={onStopEdit}
            isPersisting={isUpdating}
          />
        </>
      ) : (
        <>
          {tag.emoji && (
            <Box sx={{ display: 'flex', alignItems: 'center', pl: 0.5 }}>
              <EmojiDisplay emoji={tag.emoji} size={16} />
            </Box>
          )}
          <Typography level="body-md" sx={{ flex: 1, pl: 0.5 }}>
            {capitalizeFirstLetter(tag.name)}
          </Typography>
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={handleStartEdit}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <DeleteAction id={tag.id} name={tag.name} useDelete={useDelete} />
        </>
      )}
      <Snackbar
        color="danger"
        variant="soft"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!error}
        onClose={() => setError(null)}
      >
        {error}
      </Snackbar>
    </Box>
  );
}
